import { useTranslation } from 'react-i18next';
import { useCallback, useEffect } from 'react';
import { Trans } from 'react-i18next';
import React from 'react';

export type SupportedLanguage = 'pt-BR' | 'es-ES';

const LANGUAGE_STORAGE_KEY = 'triade-portal-language';

const DEFAULT_LANGUAGE: SupportedLanguage = 'pt-BR';

const LANGUAGE_OPTIONS_BASE = [
  { code: 'pt-BR' as SupportedLanguage, nameKey: 'portuguese', flag: '/icon-flag-brazil.svg' },
  { code: 'es-ES' as SupportedLanguage, nameKey: 'spanish', flag: '/icon-flag-es.svg' }
] as const;

export const useI18n = () => {
  const { i18n, t } = useTranslation();
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);

  const loadSavedLanguage = useCallback((): SupportedLanguage => {
    try {
      const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (savedLanguage && ['pt-BR', 'es-ES'].includes(savedLanguage)) {
        return savedLanguage as SupportedLanguage;
      }
    } catch (error) {
      console.warn('Erro ao carregar idioma do localStorage:', error);
    }
    return DEFAULT_LANGUAGE;
  }, []);

  const saveLanguage = useCallback((language: SupportedLanguage) => {
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    } catch (error) {
      console.warn('Erro ao salvar idioma no localStorage:', error);
    }
  }, []);

  useEffect(() => {
    const savedLanguage = loadSavedLanguage();
    if (i18n.language !== savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n, loadSavedLanguage]);

  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      forceUpdate();
    };
    
    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n, forceUpdate]);

  const changeLanguage = useCallback(async (language: SupportedLanguage) => {
    try {
      await i18n.changeLanguage(language);
      saveLanguage(language);
      
      window.location.reload();
    } catch (error) {
      console.error('Erro ao alterar idioma:', error);
    }
  }, [i18n, saveLanguage]);

  const getCurrentLanguage = useCallback((): SupportedLanguage => {
    return (i18n.language as SupportedLanguage) || DEFAULT_LANGUAGE;
  }, [i18n.language]);

  const isLanguage = useCallback((language: SupportedLanguage): boolean => {
    return getCurrentLanguage() === language;
  }, [getCurrentLanguage]);

  const translate = useCallback((key: string, options?: any): string => {
    return t(key, options) as string;
  }, [t]);

  const translateWithNamespace = useCallback((namespace: string, key: string, options?: any): string => {
    return t(`${namespace}:${key}`, options) as string;
  }, [t]);

  const getLanguageOptions = useCallback(() => {
    if (!i18n.isInitialized) {
      return LANGUAGE_OPTIONS_BASE.map(option => ({
        ...option,
        name: option.nameKey
      }));
    }

    const options = LANGUAGE_OPTIONS_BASE.map(option => {
      const resourceBundle = i18n.getResourceBundle(i18n.language, 'language');
      const translatedName = resourceBundle?.[option.nameKey] || option.nameKey;
      
      return {
        ...option,
        name: translatedName
      };
    });
    return options;
  }, [i18n.language, i18n.isInitialized]);

  const getCurrentLanguageName = useCallback((): string => {
    const currentLang = getCurrentLanguage();
    const options = getLanguageOptions();
    const option = options.find(opt => opt.code === currentLang);
    return option?.name || translateWithNamespace('language', 'portuguese');
  }, [getCurrentLanguage, getLanguageOptions, translateWithNamespace]);

  const getCurrentLanguageFlag = useCallback((): string => {
    const currentLang = getCurrentLanguage();
    const option = LANGUAGE_OPTIONS_BASE.find(opt => opt.code === currentLang);
    return option?.flag || '🇧🇷';
  }, [getCurrentLanguage]);

  const createTrans = useCallback((key: string, components: React.ReactElement[], values?: any) => {
    return React.createElement(Trans, {
      i18nKey: key,
      components: components,
      values: values
    });
  }, []);

  return {
    t: translate,
    changeLanguage,
    getCurrentLanguage,
    isLanguage,
    
    getCurrentLanguageName,
    getCurrentLanguageFlag,
    translateWithNamespace,
    createTrans,
    getLanguageOptions,

    currentLanguage: getCurrentLanguage(),
    isLoading: i18n.isInitialized === false,
    
    supportedLanguages: getLanguageOptions(),
    defaultLanguage: DEFAULT_LANGUAGE,
    
    Trans
  };
};

export default useI18n;
